import { NodeItem, NodeType } from '@/views/flow/nodeview/NodeItem';
import { createSingleton } from './SingletonFactory';
import AIServices from './ai/AIServices';
import { API } from '@/core/Api';
import { Node } from '@/types/ApiTypes';
import { cropToAspectRatio, isImageRatioExceeded, media } from '@/core/U';

export enum VideoModel {
  RUNWAY_VEO_FAST = 'Veo 3.1',
  RUNWAY_GEN4_TURBO = 'Runway',
  HAILUO_IMAGE_TO_VIDEO = 'Hailuo',
  VIDU_REFERENCE = 'Vidu',
  SEEDANCE = 'Seedance',
}

class VideoEngineer {
  private aiServices = new AIServices();
  private makePrompt(node: NodeItem, allNode: NodeItem[]) {
    const srcNodes = node.source.map((id) => allNode.find((n) => n.id === id)!);

    const promptNodes = srcNodes.find((n) => n.type === NodeType.PROMPT);
    const artifactNodes = srcNodes.find((n) => n.type === NodeType.ARTIFACTS);

    let finalPromptText = '';
    if (artifactNodes) {
      finalPromptText += `=== CONTEXT FROM ARTIFACT: ${artifactNodes.name} (${artifactNodes.artifact}) ===\n${artifactNodes.text}\n\n`;
    }

    if (promptNodes) {
      finalPromptText += '=== Prompt ===\n';
      finalPromptText += promptNodes.text + '\n';
    }
    const result = finalPromptText || 'An interesting animation.';
    // 999자 제한
    return result.length > 999 ? result.substring(0, 999) : result;
  }
  //기존 동기 방식
  async generate(
    videoModel: VideoModel = VideoModel.HAILUO_IMAGE_TO_VIDEO,
    aspect_ratio: string,
    node: NodeItem,
    allNode: NodeItem[],
    options?: {
      duration?: number;
      ratio?: string;
      prompt_optimizer?: boolean;
      seed?: number;
      reference_image_urls?: string[];
    },
  ): Promise<{ video: { url: string } }> {
    const prompt = this.makePrompt(node, allNode);
    const srcNodes = node.source.map((id) => allNode.find((n) => n.id === id)!);
    const imgNode = srcNodes.find((n) => n.type === NodeType.IMAGE);
    if (!imgNode) {
      return Promise.reject('no image source');
    }
    try {
      let response;
      if (videoModel === VideoModel.RUNWAY_GEN4_TURBO) {
        // Use Runway Gen4 Turbo
        response = await this.aiServices
          .getTextToVideoAI()
          .generateWithRunwayGen4(prompt, imgNode.content, {
            duration: options?.duration || 5,
            ratio: options?.ratio || '1280:720',
          });
      } else if (videoModel === VideoModel.VIDU_REFERENCE) {
        // Use Vidu Reference with multiple reference images
        const referenceUrls = options?.reference_image_urls || [imgNode.content];
        response = await this.aiServices
          .getTextToVideoAI()
          .generateWithViduReference(prompt, referenceUrls, {
            aspect_ratio: aspect_ratio || '16:9',
            seed: options?.seed,
          });
      } else {
        // Use Hailuo Image-to-Video (default)
        response = await this.aiServices
          .getTextToVideoAI()
          .generateImageToVideoWithHailuo(prompt, imgNode.content, {
            prompt_optimizer: options?.prompt_optimizer !== false,
          });
      }

      // Ensure response has the expected { video: { url } } structure
      if (response && typeof response === 'object') {
        if ('video' in response) {
          return response as { video: { url: string } };
        }
        // If response has different structure, try to extract video URL
        if ('url' in response) {
          return { video: { url: (response as any).url } };
        }
        // Look for video-like properties
        const videoProps = ['video_url', 'output_url', 'result_url'];
        for (const prop of videoProps) {
          if (prop in response) {
            return { video: { url: (response as any)[prop] } };
          }
        }
      }

      return response as { video: { url: string } };
    } catch (error) {
      return Promise.reject(error instanceof Error ? error.message : 'Video generation failed');
    }
  }

  //airflow 이용 비동기 방식
  async asynchronousGeneration(
    videoModel: VideoModel = VideoModel.HAILUO_IMAGE_TO_VIDEO,
    aspect_ratio: string,
    node: NodeItem,
    allNode: NodeItem[],
    options?: {
      duration?: number;
      ratio?: string;
      prompt_optimizer?: boolean;
      seed?: number;
      reference_image_urls?: string[];
    },
  ): Promise<any> {
    const prompt = this.makePrompt(node, allNode);
    const srcNodes = node.source.map((id) => allNode.find((n) => n.id === id)!);
    const imgNode = srcNodes.find((n) => n.type === NodeType.IMAGE);
    if (!imgNode) {
      //text To Video
      try {
        let textToVideoResponse;

        if (videoModel === VideoModel.SEEDANCE) {
          textToVideoResponse = await this.aiServices
            .getTextToVideoAI()
            .airflowGenerateWithSeedance(prompt, node, {
              aspect_ratio: aspect_ratio,
              seed: options?.seed,
              duration: options?.duration ? String(options.duration) : undefined,
            });
        } else if (videoModel === VideoModel.HAILUO_IMAGE_TO_VIDEO) {
          textToVideoResponse = await this.aiServices
            .getTextToVideoAI()
            .airflowGenerateWithHailuo(prompt, node, {
              prompt_optimizer: options?.prompt_optimizer,
            });
        } else if (videoModel === VideoModel.RUNWAY_GEN4_TURBO) {
          textToVideoResponse = await this.aiServices
            .getTextToVideoAI()
            .airflowGenerationWithRunWayGen3(prompt, node, {
              duration: options?.duration || 6,
              ratio: options?.ratio || '1280:720',
            });
        } else {
          return Promise.reject('no supported AI Model');
        }
        return Promise.resolve(textToVideoResponse);
        // console.log(options, videoModel, promptNode.text);
      } catch (error) {
        return Promise.reject(
          error instanceof Error ? error.message : 'Text to Video generation failed',
        );
      }
    } else {
      // image To Video
      try {
        const imageNodeResponse = await API.get<Node[]>(Node.API, { id: imgNode.id });
        const refreshedImgNode = imageNodeResponse.body![0];
        let imgSrc = imgNode.content;
        if (refreshedImgNode && refreshedImgNode.content) {
          // imgSrc =
          imgSrc = media(refreshedImgNode.content?.url);
          console.log(refreshedImgNode, imgNode.content);
        }

        let response;
        if (videoModel === VideoModel.RUNWAY_GEN4_TURBO) {
          // Use Runway Gen4 Turbo

          const isImageSizeExceed = await isImageRatioExceeded(imgSrc);
          if (isImageSizeExceed) {
            return Promise.reject('Image Size Exceeded Ratio 2!');
          }
          response = await this.aiServices
            .getTextToVideoAI()
            .airflowGenerateWithRunwayGen4(prompt, node, imgSrc, {
              duration: options?.duration || 6,
              ratio: options?.ratio || '1280:720',
            });
        } else if (videoModel === VideoModel.VIDU_REFERENCE) {
          // Use Vidu Reference with multiple reference images
          const referenceUrls = options?.reference_image_urls || [imgSrc];
          response = await this.aiServices
            .getTextToVideoAI()
            .airflowGenerateWithViduReference(prompt, node, referenceUrls, {
              aspect_ratio: aspect_ratio || '16:9',
              seed: options?.seed,
            });
        } else if (videoModel === VideoModel.SEEDANCE) {
          response = await this.aiServices
            .getTextToVideoAI()
            .airflowGenerateImageToVideoWithSeedance(prompt, node, imgSrc, {
              duration: options?.duration ? String(options?.duration) : undefined,
              seed: options?.seed,
              aspect_ratio: aspect_ratio || '16:9',
            });
        } else {
          // Use Hailuo Image-to-Video (default)
          response = await this.aiServices
            .getTextToVideoAI()
            .airflowGenerateImageToVideoWithHailuo(prompt, node, imgSrc, {
              prompt_optimizer: options?.prompt_optimizer !== false,
            });
        }
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(
          error instanceof Error ? error.message : 'Image to Video generation failed',
        );
      }
    }
  }

  async backgroundGeneration(
    videoModel: VideoModel = VideoModel.HAILUO_IMAGE_TO_VIDEO,
    aspect_ratio: string,
    node: NodeItem,
    allNode: NodeItem[],
    options?: {
      duration?: number;
      ratio?: string;
      prompt_optimizer?: boolean;
      seed?: number;
      reference_image_urls?: string[];
    },
  ): Promise<any> {
    const prompt = this.makePrompt(node, allNode);
    const srcNodes = node.source.map((id) => allNode.find((n) => n.id === id)!);
    const imgNode = srcNodes.find((n) => n.type === NodeType.IMAGE);

    if (!imgNode) {
      //text To Video
      try {
        let textToVideoResponse;

        if (videoModel === VideoModel.SEEDANCE) {
          textToVideoResponse = await this.aiServices
            .getTextToVideoAI()
            .backgroundGenerateWithSeedance(prompt, node, {
              aspect_ratio: aspect_ratio,
              seed: options?.seed,
              duration: options?.duration ? String(options.duration) : undefined,
            });
        } else if (videoModel === VideoModel.HAILUO_IMAGE_TO_VIDEO) {
          textToVideoResponse = await this.aiServices
            .getTextToVideoAI()
            .backgroundGenerateWithHailuo(prompt, node, {
              prompt_optimizer: options?.prompt_optimizer,
            });
        } else if (videoModel === VideoModel.RUNWAY_VEO_FAST) {
          textToVideoResponse = await this.aiServices
            .getTextToVideoAI()
            .backgroundGenerationWithRunWayGen3(prompt, node, {
              duration: options?.duration || 6,
              ratio: options?.ratio || '1280:720',
            });
        } else {
          return Promise.reject('no supported AI Model');
        }
        return Promise.resolve(textToVideoResponse);
        // console.log(options, videoModel, promptNode.text);
      } catch (error) {
        return Promise.reject(
          error instanceof Error ? error.message : 'Text to Video generation failed',
        );
      }
    } else {
      // image To Video
      try {
        const imageNodeResponse = await API.get<Node[]>(Node.API, { id: imgNode.id });
        const refreshedImgNode = imageNodeResponse.body![0];
        let imgSrc = imgNode.content;
        if (refreshedImgNode && refreshedImgNode.content) {
          // imgSrc =
          imgSrc = media(refreshedImgNode.content?.url);
          console.log(refreshedImgNode, imgNode.content);
        }

        let response;
        if (videoModel === VideoModel.RUNWAY_GEN4_TURBO) {
          // Use Runway Gen4 Turbo

          const isImageSizeExceed = await isImageRatioExceeded(imgSrc);
          if (isImageSizeExceed) {
            return Promise.reject('Image Size Exceeded Ratio 2!');
          }
          response = await this.aiServices
            .getTextToVideoAI()
            .backgroundGenerateWithRunwayGen4(prompt, node, imgSrc, {
              duration: options?.duration || 6,
              ratio: options?.ratio || '1280:720',
            });
        } else if (videoModel === VideoModel.VIDU_REFERENCE) {
          // Use Vidu Reference with multiple reference images
          const referenceUrls = options?.reference_image_urls || [imgSrc];
          response = await this.aiServices
            .getTextToVideoAI()
            .backgroundGenerateWithViduReference(prompt, node, referenceUrls, {
              aspect_ratio: aspect_ratio || '16:9',
              seed: options?.seed,
            });
        } else if (videoModel === VideoModel.RUNWAY_VEO_FAST) {
          // resizing
          const ratio = Number(node.sizeText.split(':')[0]) / Number(node.sizeText.split(':')[1]);
          const dataUrl = await cropToAspectRatio(imgSrc, ratio);

          response = await this.aiServices
            .getTextToVideoAI()
            .backgroundGenerationWithRunWayGen3(prompt, node, {
              duration: options?.duration || 6,
              ratio: options?.ratio || '1280:720',
              prompt_image: dataUrl,
            });
        }
        // else if (videoModel === VideoModel.SEEDANCE) {
        //   response = await this.aiServices
        //     .getTextToVideoAI()
        //     .backgroundGenerateImageToVideoWithSeedance(prompt, node, imgSrc, {
        //       duration: options?.duration ? String(options?.duration) : undefined,
        //       seed: options?.seed,
        //       aspect_ratio: aspect_ratio || '16:9',
        //     });
        // }
        else {
          // Use Hailuo Image-to-Video (default)
          response = await this.aiServices
            .getTextToVideoAI()
            .backgroundGenerateImageToVideoWithHailuo(prompt, node, imgSrc, {
              prompt_optimizer: options?.prompt_optimizer !== false,
            });
        }
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(
          error instanceof Error ? error.message : 'Image to Video generation failed',
        );
      }
    }
  }
  // airflow cancel
  async cancelGeneration(nodeId: number) {
    // await API.delete(`/api/airflow/cancel/${nodeId}`); -> airflow
    await API.delete(`/api/node/cancel/generation/${nodeId}`); // -> background
  }
}

export default createSingleton(VideoEngineer);
